import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../App";
import AdminLayout from "../../components/admin/AdminLayout";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Progress } from "../../components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { toast } from "sonner";
import {
  ArrowLeft,
  Briefcase,
  Clock,
  DollarSign,
  Edit,
  Save,
  Timer,
  MessageSquare,
  Users,
  Building2,
  Calendar,
  CheckCircle2,
  CircleDashed,
  PlayCircle,
  PauseCircle,
  Loader2,
  Plus,
  RefreshCw,
  Archive,
  ArchiveRestore,
  SlidersHorizontal,
  History,
  FileText,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";

// Project phases/categories for time tracking
const PROJECT_PHASES = {
  discovery: { name: "Discovery & Assessment" },
  planning: { name: "Planning & Strategy" },
  implementation: { name: "Implementation" },
  training: { name: "Training & Education" },
  review: { name: "Review & Optimization" },
  compliance: { name: "Compliance & Audit" },
  support: { name: "Ongoing Support" },
  documentation: { name: "Documentation" },
  meetings: { name: "Meetings & Calls" },
  research: { name: "Research & Analysis" },
  other: { name: "Other" },
};

// Project status configuration
const PROJECT_STATUS_CONFIG = {
  not_started: { label: "Not Started", color: "bg-gray-100 text-gray-700", icon: CircleDashed },
  in_progress: { label: "In Progress", color: "bg-blue-100 text-blue-700", icon: PlayCircle },
  completed: { label: "Completed", color: "bg-green-100 text-green-700", icon: CheckCircle2 },
  on_hold: { label: "On Hold", color: "bg-amber-100 text-amber-700", icon: PauseCircle },
};

const AdminProjectDetail = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { token, API, user, isSuperAdmin } = useAuth();
  
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  
  // Consultants list for assignment
  const [consultants, setConsultants] = useState([]);
  const [companyUsers, setCompanyUsers] = useState([]);
  
  // Time entries
  const [timeEntries, setTimeEntries] = useState([]);
  const [timeForm, setTimeForm] = useState({
    hours: "",
    description: "",
    category: "other",
    date: new Date().toISOString().split("T")[0],
  });
  
  // Project updates
  const [projectUpdates, setProjectUpdates] = useState([]);
  const [updateForm, setUpdateForm] = useState({
    title: "",
    content: "",
    update_type: "progress",
  });
  
  // Hours reset
  const [resettingHours, setResettingHours] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  
  // Hours adjustment
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [adjusting, setAdjusting] = useState(false);
  const [adjustmentLog, setAdjustmentLog] = useState([]);
  const [adjustForm, setAdjustForm] = useState({
    field: "monthly_hours",
    new_value: "",
    note: "",
  });
  
  // Edit form
  const [editForm, setEditForm] = useState({
    name: "",
    description: "",
    monthly_hours: 40,
    hourly_rate: 150,
    hours_rollover: false,
    hours_reset_day: 1,
    lead_consultant_id: "",
    additional_consultants: [],
    project_status: "not_started",
  });

  useEffect(() => {
    fetchProject();
    fetchConsultants();
    fetchAdjustmentLog();
  }, [projectId]);

  const fetchProject = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/projects/${projectId}/details`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        setProject(data);
        setEditForm({
          name: data.name || "",
          description: data.description || "",
          monthly_hours: data.monthly_hours || 40,
          hourly_rate: data.hourly_rate || 150,
          hours_rollover: data.hours_rollover || false,
          hours_reset_day: data.hours_reset_day || 1,
          lead_consultant_id: data.lead_consultant_id || "",
          additional_consultants: data.additional_consultants || [],
          project_status: data.project_status || "not_started",
        });
        
        // Fetch related data
        fetchTimeEntries();
        fetchProjectUpdates();
        if (data.company_id) {
          fetchCompanyUsers(data.company_id);
        }
      } else {
        toast.error("Project not found");
        navigate("/admin/projects");
      }
    } catch (error) {
      toast.error("Failed to load project");
    } finally {
      setLoading(false);
    }
  };

  const fetchConsultants = async () => {
    try {
      const response = await fetch(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        const adminUsers = data.filter(u => 
          u.role === "admin" || u.role === "super_admin" || u.role === "consultant"
        );
        setConsultants(adminUsers);
      }
    } catch (error) {
      console.error("Request failed:", error);
    }
  };

  const fetchCompanyUsers = async (companyId) => {
    try {
      const response = await fetch(`${API}/companies/${companyId}/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setCompanyUsers(data);
      }
    } catch (error) {
      console.error("Request failed:", error);
    }
  };

  const fetchTimeEntries = async () => {
    try {
      const response = await fetch(`${API}/admin/time-entries/project/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setTimeEntries(data.entries || []);
      }
    } catch (error) {
      console.error("Request failed:", error);
    }
  };

  const fetchProjectUpdates = async () => {
    try {
      const response = await fetch(`${API}/admin/projects/${projectId}/updates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setProjectUpdates(data);
      }
    } catch (error) {
      console.error("Request failed:", error);
    }
  };

  const handleResetHours = async () => {
    setResettingHours(true);
    try {
      const response = await fetch(`${API}/admin/projects/${projectId}/reset-hours`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(result.message || "Hours reset successfully");
        setShowResetConfirm(false);
        fetchProject(); // Refresh project data
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to reset hours");
      }
    } catch (error) {
      toast.error("Failed to reset hours");
    } finally {
      setResettingHours(false);
    }
  };

  const fetchAdjustmentLog = async () => {
    try {
      const response = await fetch(
        `${API}/admin/projects/${projectId}/adjustment-log`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setAdjustmentLog(data);
      }
    } catch {
      // silent
    }
  };

  const handleAdjustHours = async () => {
    if (!adjustForm.note.trim()) {
      toast.error("Please enter a reason for this adjustment");
      return;
    }
    const val = parseFloat(adjustForm.new_value);
    if (isNaN(val) || val < 0) {
      toast.error("Please enter a valid non-negative number");
      return;
    }

    setAdjusting(true);
    try {
      const params = new URLSearchParams({
        field: adjustForm.field,
        new_value: val.toString(),
        note: adjustForm.note.trim(),
      });
      const response = await fetch(
        `${API}/admin/projects/${projectId}/adjust-hours?${params.toString()}`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message || "Hours adjusted successfully");
        setShowAdjustModal(false);
        setAdjustForm({ field: "monthly_hours", new_value: "", note: "" });
        fetchProject();
        fetchAdjustmentLog();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to adjust hours");
      }
    } catch {
      toast.error("Failed to adjust hours");
    } finally {
      setAdjusting(false);
    }
  };

  const handleSaveProject = async () => {
    setSaving(true);
    try {
      const params = new URLSearchParams({
        name: editForm.name,
        description: editForm.description,
        monthly_hours: editForm.monthly_hours.toString(),
        hourly_rate: editForm.hourly_rate.toString(),
        hours_rollover: editForm.hours_rollover.toString(),
        hours_reset_day: editForm.hours_reset_day.toString(),
        project_status: editForm.project_status,
      });
      
      if (editForm.lead_consultant_id) {
        params.append("lead_consultant_id", editForm.lead_consultant_id);
      } else {
        params.append("lead_consultant_id", "none");
      }
      
      params.append("additional_consultant_ids", editForm.additional_consultants.join(","));
      
      const response = await fetch(`${API}/admin/projects/${projectId}?${params.toString()}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        toast.success("Project updated successfully");
        setIsEditing(false);
        fetchProject();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update project");
      }
    } catch (error) {
      toast.error("Failed to update project");
    } finally {
      setSaving(false);
    }
  };

  const handleAddTimeEntry = async () => {
    if (!timeForm.hours || parseFloat(timeForm.hours) <= 0) {
      toast.error("Please enter valid hours");
      return;
    }
    if (!timeForm.description.trim()) {
      toast.error("Please enter a description");
      return;
    }
    
    setSaving(true);
    try {
      const params = new URLSearchParams({
        project_id: projectId,
        hours: timeForm.hours,
        description: timeForm.description,
        category: timeForm.category,
        date: timeForm.date,
      });
      
      const response = await fetch(`${API}/admin/time-entries?${params.toString()}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        toast.success("Time entry added");
        setTimeForm({
          hours: "",
          description: "",
          category: "other",
          date: new Date().toISOString().split("T")[0],
        });
        fetchTimeEntries();
        fetchProject();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to add time entry");
      }
    } catch (error) {
      toast.error("Failed to add time entry");
    } finally {
      setSaving(false);
    }
  };

  const handleAddUpdate = async () => {
    if (!updateForm.title.trim()) {
      toast.error("Please enter a title");
      return;
    }
    
    setSaving(true);
    try {
      const params = new URLSearchParams({
        title: updateForm.title,
        content: updateForm.content,
        update_type: updateForm.update_type,
      });
      
      const response = await fetch(`${API}/admin/projects/${projectId}/update?${params.toString()}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        toast.success("Update posted");
        setUpdateForm({ title: "", content: "", update_type: "progress" });
        fetchProjectUpdates();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to post update");
      }
    } catch (error) {
      toast.error("Failed to post update");
    } finally {
      setSaving(false);
    }
  };

  const handleArchiveProject = async () => {
    const newStatus = project?.status === "archived" ? "active" : "archived";
    setSaving(true);
    try {
      const response = await fetch(`${API}/admin/projects/${projectId}?status=${newStatus}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        toast.success(newStatus === "archived" ? "Project archived" : "Project restored");
        fetchProject();
      } else {
        toast.error("Failed to update project status");
      }
    } catch (error) {
      toast.error("Failed to update project");
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = PROJECT_STATUS_CONFIG[status] || PROJECT_STATUS_CONFIG.not_started;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-[#2471A3]" />
        </div>
      </AdminLayout>
    );
  }

  if (!project) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <p className="text-gray-500">Project not found</p>
          <Button onClick={() => navigate("/admin/projects")} className="mt-4">
            Back to Projects
          </Button>
        </div>
      </AdminLayout>
    );
  }

  const hoursUsed = project.hours_used || 0;
  const hoursRemaining = project.hours_remaining || project.monthly_hours;
  const hoursPercentage = (hoursUsed / project.monthly_hours) * 100;

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate("/admin/projects")}
              data-testid="back-to-projects"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-[#1A2636]">{project.name}</h1>
                {getStatusBadge(project.project_status)}
                {project.status === "archived" && (
                  <Badge variant="secondary">Archived</Badge>
                )}
              </div>
              <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                <Building2 className="w-4 h-4" />
                {project.company_name || "Unknown Company"}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {isEditing ? (
              <>
                <Button variant="outline" onClick={() => setIsEditing(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleSaveProject}
                  disabled={saving}
                  className="bg-[#2471A3] hover:bg-[#2471A3]/90"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Changes
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setIsEditing(true)}>
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Project
                </Button>
                <Button 
                  variant={project.status === "archived" ? "default" : "outline"}
                  onClick={handleArchiveProject}
                  disabled={saving}
                  className={project.status === "archived" ? "bg-green-600 hover:bg-green-700" : ""}
                >
                  {project.status === "archived" ? (
                    <><ArchiveRestore className="w-4 h-4 mr-2" /> Restore</>
                  ) : (
                    <><Archive className="w-4 h-4 mr-2" /> Archive</>
                  )}
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Column - Project Details & Settings */}
          <div className="lg:col-span-2 space-y-6">
            {/* Project Info Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-[#2471A3]" />
                  Project Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {isEditing ? (
                  <>
                    <div>
                      <Label>Project Name</Label>
                      <Input
                        value={editForm.name}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                        data-testid="edit-project-name"
                      />
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Textarea
                        value={editForm.description}
                        onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                        rows={3}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Monthly Hours</Label>
                        <Input
                          type="number"
                          value={editForm.monthly_hours}
                          onChange={(e) => setEditForm({ ...editForm, monthly_hours: parseInt(e.target.value) || 0 })}
                        />
                      </div>
                      {/* Only allow super admins to edit hourly rate */}
                      {isSuperAdmin && (
                        <div>
                          <Label>Hourly Rate ($)</Label>
                          <Input
                            type="number"
                            value={editForm.hourly_rate}
                            onChange={(e) => setEditForm({ ...editForm, hourly_rate: parseFloat(e.target.value) || 0 })}
                          />
                        </div>
                      )}
                    </div>
                    <div>
                      <Label>Project Status</Label>
                      <Select
                        value={editForm.project_status}
                        onValueChange={(value) => setEditForm({ ...editForm, project_status: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(PROJECT_STATUS_CONFIG).map(([key, { label }]) => (
                            <SelectItem key={key} value={key}>{label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={editForm.hours_rollover}
                        onCheckedChange={(checked) => setEditForm({ ...editForm, hours_rollover: checked })}
                      />
                      <Label>Allow hours rollover to next month</Label>
                    </div>
                    <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <Label className="flex items-center gap-2 mb-2">
                        <Calendar className="w-4 h-4 text-blue-600" />
                        Monthly Hours Reset Day
                      </Label>
                      <div className="flex items-center gap-3">
                        <Select
                          value={editForm.hours_reset_day.toString()}
                          onValueChange={(value) => setEditForm({ ...editForm, hours_reset_day: parseInt(value) })}
                        >
                          <SelectTrigger className="w-[140px]" data-testid="hours-reset-day-select">
                            <SelectValue placeholder="Select day" />
                          </SelectTrigger>
                          <SelectContent>
                            {Array.from({ length: 28 }, (_, i) => i + 1).map((day) => (
                              <SelectItem key={day} value={day.toString()}>
                                {day === 1 ? "1st" : day === 2 ? "2nd" : day === 3 ? "3rd" : `${day}th`} of month
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <span className="text-sm text-gray-600">
                          Hours reset at 2 AM UTC on this day each month
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        {editForm.hours_rollover 
                          ? "Unused hours will roll over to the next month on this date."
                          : "Hours will reset to the monthly allocation. Unused hours will be lost."}
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    {project.description && (
                      <p className="text-gray-600">{project.description}</p>
                    )}
                    <div className={`grid grid-cols-2 ${isSuperAdmin ? 'md:grid-cols-4' : 'md:grid-cols-3'} gap-4 pt-4 border-t`}>
                      <div className="text-center p-3 bg-gray-50 rounded-lg">
                        <Clock className="w-5 h-5 mx-auto text-gray-400 mb-1" />
                        <p className="text-2xl font-bold text-[#1A2636]">{project.monthly_hours}</p>
                        <p className="text-xs text-gray-500">Hours/Month</p>
                      </div>
                      {/* Only show hourly rate to super admins */}
                      {isSuperAdmin && (
                        <div className="text-center p-3 bg-gray-50 rounded-lg">
                          <DollarSign className="w-5 h-5 mx-auto text-gray-400 mb-1" />
                          <p className="text-2xl font-bold text-[#1A2636]">${project.hourly_rate}</p>
                          <p className="text-xs text-gray-500">Hourly Rate</p>
                        </div>
                      )}
                      <div className="text-center p-3 bg-blue-50 rounded-lg">
                        <Timer className="w-5 h-5 mx-auto text-blue-500 mb-1" />
                        <p className="text-2xl font-bold text-blue-600">{hoursUsed.toFixed(1)}</p>
                        <p className="text-xs text-gray-500">Hours Used</p>
                      </div>
                      <div className="text-center p-3 bg-green-50 rounded-lg">
                        <CheckCircle2 className="w-5 h-5 mx-auto text-green-500 mb-1" />
                        <p className="text-2xl font-bold text-green-600">{hoursRemaining.toFixed(1)}</p>
                        <p className="text-xs text-gray-500">Remaining</p>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-500">Hours Progress</span>
                        <span className="font-medium">{hoursPercentage.toFixed(0)}%</span>
                      </div>
                      <Progress value={Math.min(hoursPercentage, 100)} className="h-2" />
                    </div>
                    {/* Hours Reset Info */}
                    <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-blue-600" />
                          <span className="text-sm font-medium text-gray-700">
                            Hours Reset: {project.hours_reset_day || 1}{project.hours_reset_day === 1 ? "st" : project.hours_reset_day === 2 ? "nd" : project.hours_reset_day === 3 ? "rd" : "th"} of each month
                          </span>
                        </div>
                        <Badge variant={project.hours_rollover ? "secondary" : "outline"} className={project.hours_rollover ? "bg-green-100 text-green-700" : ""}>
                          {project.hours_rollover ? "Rollover ON" : "No Rollover"}
                        </Badge>
                      </div>
                      {project.rollover_hours > 0 && (
                        <p className="text-xs text-blue-600 mt-1">
                          +{project.rollover_hours} rollover hours from previous months
                        </p>
                      )}
                      {project.last_hours_reset_at && (
                        <p className="text-xs text-gray-500 mt-1">
                          Last reset: {new Date(project.last_hours_reset_at).toLocaleDateString()}
                        </p>
                      )}
                      {/* Manual Reset Button - Super Admin Only */}
                      {isSuperAdmin && (
                        <div className="mt-3 pt-3 border-t border-blue-200 flex flex-wrap items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShowResetConfirm(true)}
                            className="text-amber-600 border-amber-300 hover:bg-amber-50"
                            data-testid="reset-hours-btn"
                          >
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Reset Hours Now
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setAdjustForm({
                                field: "monthly_hours",
                                new_value: "",
                                note: "",
                              });
                              setShowAdjustModal(true);
                            }}
                            className="text-[#2471A3] border-[#2471A3]/30 hover:bg-blue-50"
                            data-testid="adjust-hours-btn"
                          >
                            <SlidersHorizontal className="w-4 h-4 mr-2" />
                            Adjust Hours
                          </Button>
                          <p className="w-full text-xs text-gray-500 mt-1">
                            {project.hours_rollover 
                              ? "Unused hours will be added to rollover balance"
                              : "Hours will reset to monthly allocation. Unused hours will be lost."}
                          </p>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Consultant Assignment Card */}
            {(isEditing || isSuperAdmin) && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-[#2471A3]" />
                    Consultant Assignment
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {isEditing ? (
                    <>
                      <div>
                        <Label>Lead Consultant</Label>
                        <Select
                          value={editForm.lead_consultant_id || "none"}
                          onValueChange={(value) => setEditForm({ ...editForm, lead_consultant_id: value === "none" ? "" : value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select lead consultant..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No lead consultant assigned</SelectItem>
                            {consultants.map((c) => (
                              <SelectItem key={c.user_id} value={c.user_id}>
                                {c.name || c.email}
                                {c.role === "super_admin" && " (Super Admin)"}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Additional Consultants</Label>
                        <div className="border rounded-lg p-3 max-h-40 overflow-y-auto space-y-2">
                          {consultants
                            .filter(c => c.user_id !== editForm.lead_consultant_id)
                            .map((c) => (
                              <label key={c.user_id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={editForm.additional_consultants.includes(c.user_id)}
                                  onChange={() => {
                                    const updated = editForm.additional_consultants.includes(c.user_id)
                                      ? editForm.additional_consultants.filter(id => id !== c.user_id)
                                      : [...editForm.additional_consultants, c.user_id];
                                    setEditForm({ ...editForm, additional_consultants: updated });
                                  }}
                                  className="rounded"
                                />
                                <span className="text-sm">{c.name || c.email}</span>
                              </label>
                            ))}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm text-gray-500 mb-1">Lead Consultant</p>
                        {project.lead_consultant ? (
                          <p className="font-medium">{project.lead_consultant.name || project.lead_consultant.email}</p>
                        ) : (
                          <p className="text-gray-400 italic">Not assigned</p>
                        )}
                      </div>
                      {project.additional_consultants_info?.length > 0 && (
                        <div>
                          <p className="text-sm text-gray-500 mb-1">Additional Consultants</p>
                          <div className="flex flex-wrap gap-2">
                            {project.additional_consultants_info.map((c) => (
                              <Badge key={c.user_id} variant="secondary">{c.name || c.email}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Time Tracking Tab Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Timer className="w-5 h-5 text-[#2471A3]" />
                  Time Tracking
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="log" className="w-full">
                  <TabsList className="mb-4">
                    <TabsTrigger value="log">Log Time</TabsTrigger>
                    <TabsTrigger value="history">History ({timeEntries.length})</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="log" className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Hours *</Label>
                        <Input
                          type="number"
                          min="0.25"
                          step="0.25"
                          value={timeForm.hours}
                          onChange={(e) => setTimeForm({ ...timeForm, hours: e.target.value })}
                          placeholder="e.g., 2.5"
                          data-testid="time-hours-input"
                        />
                      </div>
                      <div>
                        <Label>Date</Label>
                        <Input
                          type="date"
                          value={timeForm.date}
                          onChange={(e) => setTimeForm({ ...timeForm, date: e.target.value })}
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Category</Label>
                      <Select
                        value={timeForm.category}
                        onValueChange={(value) => setTimeForm({ ...timeForm, category: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(PROJECT_PHASES).map(([key, { name }]) => (
                            <SelectItem key={key} value={key}>{name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Description *</Label>
                      <Textarea
                        value={timeForm.description}
                        onChange={(e) => setTimeForm({ ...timeForm, description: e.target.value })}
                        placeholder="What did you work on?"
                        rows={2}
                      />
                    </div>
                    <Button 
                      onClick={handleAddTimeEntry}
                      disabled={saving || !timeForm.hours || !timeForm.description.trim()}
                      className="w-full bg-[#2471A3] hover:bg-[#2471A3]/90"
                      data-testid="save-time-entry-btn"
                    >
                      {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                      Add Time Entry
                    </Button>
                  </TabsContent>
                  
                  <TabsContent value="history">
                    {timeEntries.length === 0 ? (
                      <p className="text-center text-gray-500 py-8">No time entries yet</p>
                    ) : (
                      <div className="space-y-2 max-h-80 overflow-y-auto">
                        {timeEntries.map((entry) => (
                          <div key={entry.entry_id} className="p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <p className="font-medium text-sm">{entry.description}</p>
                                <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                                  <span>{entry.date}</span>
                                  <Badge variant="outline" className="text-xs">{entry.category_name || entry.category}</Badge>
                                  <span>by {entry.consultant_name}</span>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="font-bold text-[#2471A3]">{entry.hours}h</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Updates & Activity */}
          <div className="space-y-6">
            {/* Project Updates Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-[#1FC6B7]" />
                  Project Updates
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Add Update Form */}
                <div className="space-y-3 pb-4 border-b">
                  <Input
                    placeholder="Update title..."
                    value={updateForm.title}
                    onChange={(e) => setUpdateForm({ ...updateForm, title: e.target.value })}
                    data-testid="update-title-input"
                  />
                  <Select
                    value={updateForm.update_type}
                    onValueChange={(value) => setUpdateForm({ ...updateForm, update_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="progress">Progress Update</SelectItem>
                      <SelectItem value="milestone">Milestone</SelectItem>
                      <SelectItem value="blocker">Blocker/Issue</SelectItem>
                      <SelectItem value="note">General Note</SelectItem>
                    </SelectContent>
                  </Select>
                  <Textarea
                    placeholder="Update details..."
                    value={updateForm.content}
                    onChange={(e) => setUpdateForm({ ...updateForm, content: e.target.value })}
                    rows={2}
                  />
                  <Button 
                    onClick={handleAddUpdate}
                    disabled={saving || !updateForm.title.trim()}
                    className="w-full bg-[#1FC6B7] hover:bg-[#1FC6B7]/90"
                    data-testid="post-update-btn"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Post Update
                  </Button>
                </div>
                
                {/* Updates List */}
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {projectUpdates.length === 0 ? (
                    <p className="text-center text-gray-500 py-4">No updates yet</p>
                  ) : (
                    projectUpdates.map((update) => (
                      <div key={update.update_id} className="p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-sm">{update.title}</span>
                          <Badge variant="outline" className="text-xs capitalize">{update.update_type}</Badge>
                        </div>
                        {update.content && (
                          <p className="text-sm text-gray-600 line-clamp-3">{update.content}</p>
                        )}
                        <p className="text-xs text-gray-400 mt-2">
                          {new Date(update.created_at).toLocaleDateString()} • {update.created_by_name}
                        </p>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Assigned Clients Card */}
            {companyUsers.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Users className="w-4 h-4 text-gray-500" />
                    Company Team ({companyUsers.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {companyUsers.map((u) => (
                      <div key={u.user_id} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                        <div className="w-8 h-8 rounded-full bg-[#2471A3] flex items-center justify-center text-white text-xs font-medium">
                          {(u.name || u.email || "?").charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{u.name || u.email}</p>
                          <p className="text-xs text-gray-500 truncate">{u.email}</p>
                        </div>
                        {u.is_company_admin && (
                          <Badge variant="secondary" className="text-xs">Admin</Badge>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Hours Adjustment History */}
            {adjustmentLog.length > 0 && (
              <Card data-testid="adjustment-history-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <History className="w-4 h-4 text-[#2471A3]" />
                    Hours Adjustments ({adjustmentLog.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-80 overflow-y-auto">
                    {adjustmentLog.map((entry) => {
                      const fieldLabel = {
                        monthly_hours: "Monthly Hours",
                        rollover_hours: "Rollover Hours",
                        used_hours: "Used Hours",
                      }[entry.field] || entry.field;
                      return (
                        <div
                          key={entry.adjustment_id}
                          className="p-3 bg-gray-50 rounded-lg border-l-3 border-l-[#2471A3]"
                          data-testid={`adjustment-entry-${entry.adjustment_id}`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-[#1A2636]">
                                {fieldLabel}: {entry.old_value} → {entry.new_value}
                              </p>
                              <div className="flex items-center gap-1 mt-1">
                                <FileText className="w-3 h-3 text-gray-400 shrink-0" />
                                <p className="text-xs text-gray-600 italic line-clamp-2">{entry.note}</p>
                              </div>
                            </div>
                          </div>
                          <p className="text-xs text-gray-400 mt-2">
                            {new Date(entry.created_at).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                              hour: "numeric",
                              minute: "2-digit",
                            })} by {entry.adjusted_by_name}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Hours Reset Confirmation Dialog */}
      <AlertDialog open={showResetConfirm} onOpenChange={setShowResetConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-amber-500" />
              Reset Project Hours
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>Are you sure you want to reset hours for <strong>{project?.name}</strong>?</p>
              {project?.hours_rollover ? (
                <p className="text-green-600 bg-green-50 p-2 rounded">
                  Rollover is enabled. Unused hours ({hoursRemaining.toFixed(1)} hrs) will be added to the rollover balance.
                </p>
              ) : (
                <p className="text-amber-600 bg-amber-50 p-2 rounded">
                  Rollover is disabled. Unused hours ({hoursRemaining.toFixed(1)} hrs) will be lost.
                </p>
              )}
              <p className="text-sm text-gray-500 mt-2">
                Monthly hours will reset to {project?.monthly_hours} hours.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={resettingHours}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleResetHours}
              disabled={resettingHours}
              className="bg-amber-500 hover:bg-amber-600"
            >
              {resettingHours ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Resetting...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Reset Hours Now
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Hours Adjustment Dialog */}
      <Dialog open={showAdjustModal} onOpenChange={setShowAdjustModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <SlidersHorizontal className="w-5 h-5 text-[#2471A3]" />
              Adjust Project Hours
            </DialogTitle>
            <DialogDescription>
              Manually adjust hours for <strong>{project?.name}</strong>. A reason note is required for audit tracking.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Field to Adjust</Label>
              <Select
                value={adjustForm.field}
                onValueChange={(value) => {
                  const currentVal = project?.[value] || 0;
                  setAdjustForm({ ...adjustForm, field: value, new_value: currentVal.toString() });
                }}
              >
                <SelectTrigger data-testid="adjust-field-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly_hours">Monthly Allotted Hours</SelectItem>
                  <SelectItem value="rollover_hours">Rollover Hours</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                Current {adjustForm.field === "monthly_hours" ? "Monthly Hours" : "Rollover Hours"}:{" "}
                <strong>{project?.[adjustForm.field] || 0}</strong>
              </p>
            </div>
            <div>
              <Label>New Value</Label>
              <Input
                type="number"
                min="0"
                step="0.5"
                value={adjustForm.new_value}
                onChange={(e) => setAdjustForm({ ...adjustForm, new_value: e.target.value })}
                placeholder="Enter new hour value"
                data-testid="adjust-value-input"
              />
            </div>
            <div>
              <Label>Reason for Adjustment *</Label>
              <Textarea
                value={adjustForm.note}
                onChange={(e) => setAdjustForm({ ...adjustForm, note: e.target.value })}
                placeholder="e.g., Rollover hours expired per contract terms, client requested increase, etc."
                rows={3}
                data-testid="adjust-note-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowAdjustModal(false)}
              disabled={adjusting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAdjustHours}
              disabled={adjusting || !adjustForm.note.trim() || adjustForm.new_value === ""}
              className="bg-[#2471A3] hover:bg-[#2471A3]/90"
              data-testid="confirm-adjust-btn"
            >
              {adjusting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
              ) : (
                <><SlidersHorizontal className="w-4 h-4 mr-2" /> Apply Adjustment</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminProjectDetail;
